/**
 * CoverBody
 * 
 * @Author: Focci
 * @Date: 2023-10-09 12:11:31
 * @Last Modified by: Focci
 * @Last Modified time: 2023-10-09 12:11:31
 */

'use client'

import PropTypes from 'prop-types'
import Chips from '@page/common/card/property/Chips'
import Picture from '@comp/Picture'
import useApp from '@hook/useApp'
import useLazyComponent from '@hook/useLazyComponent'
import { imageSize } from '@lib/image/loader'
import { useCallback, useEffect } from 'react'
import { useTranslations } from 'next-intl'
import { getCoverOptions, getImageAlt } from './lib'

export default function CoverBody({
  house = {},
  onClick = () => {},
}) {
  const ts = useTranslations('search')
  const th = useTranslations('house')
  const tp = useTranslations('profile')
  const imgAlt = getImageAlt(house, tp)
  const { isTablet } = useApp()
  const { options, allImages } = getCoverOptions({ house, ts, th })
  const len = options.length

  // 点击图片
  const handleClick = useCallback((item) => {
    const type = item.key ? item.key : 'all'
    const index = allImages.indexOf(item.url)

    onClick(type, index)
  }, [allImages, onClick])

  // 响应式图片可以滚动
  // 实现原理是在第一张图片上覆盖一层 swiper
  const { Wedget: ImageSwiperForTablet, getWedget, ready } = useLazyComponent()
  useEffect(() => {
    isTablet && getWedget(() => import('./ImageSwiperForTablet'))
  }, [getWedget, isTablet])

  return (
    <div className="max-w-[1680px] mx-auto overflow-hidden h-full relative">
      <div className={['h-full', len > 0 ? 'grid grid-rows-2 grid-cols-1 lg:grid-cols-4 gap-1' : ''].join(' ')}>
        {options.map((item, index) => {
          const width = len === 1 ? 1500 : (index === 0 ? 838 : 416)
          const height = len === 1 ? 860 : (index === 0 ? 640 : 318)

          const className = ['relative']
          
          if(len === 1) {
            className.push('col-span-4 row-span-2')
          } else if(len === 2) {
            className.push('col-span-2 row-span-2', index > 0 ? 'hidden lg:block' : '')
          } else {
            className.push(index === 0 ? 'col-span-2 row-span-2' : '', index > 0 ? 'hidden lg:block' : '')
          }

          return (
            <div 
              className={className.join(' ')} 
              key={item.url + index}
              role="button"
              tabIndex="0"
              onClick={() => handleClick(item)}
              onKeyPress={() => handleClick(item)}
            >
              <Picture 
                src={imageSize(item.url, width, height) || ''}
                mobileSrc={imageSize(options?.[0]?.url, 430, 280) || ''}
                wrapClassName="bg-cf0"
                mobileWidth={430}
                mobileHeight={280}
                width={width}
                height={height}
                alt={imgAlt}
              />
              {index > 0 && item.label && item.icon && (
                <div className="absolute bg-black/30 inset-0 flex items-center justify-center">
                  <div className="text-white flex flex-col items-center gap-y-2">
                    {item.icon}
                    <span className="text-f.9">
                      {item.label}
                    </span>
                  </div>
                </div>
              )}
              {isTablet && ready && index === 0 && (
                <ImageSwiperForTablet house={house} images={house.images} />
              )}
            </div>
          )
        })}
        <div className="absolute left-4 top-4 z-1 pr-8 pointer-events-none">
          <Chips {...house} />
        </div>
      </div>
    </div>
  )
}

CoverBody.propTypes = {
  house: PropTypes.object,
  onClick: PropTypes.func,
}
